import React from 'react';
import {
  Form,
  Input,
  Select,
  Card,
  Typography,
  Row,
  Col,
  Checkbox,
  Divider,
  Switch,
  InputNumber
} from 'antd';
import { 
  UserOutlined, 
  MailOutlined, 
  PhoneOutlined,
  FlagOutlined
} from '@ant-design/icons';
import moment from 'moment';
import { US_STATES, formatDuration } from '../constants';

const { Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const Step2UserInfo = ({
  form,
  formValues,
  selectedDate,
  selectedEndDate,
  selectedTimeSlot,
  selectedEventType,
  storageUnits = [],
  forceMobileEvent = false
}) => (
  <div style={{ maxHeight: '60vh', overflowY: 'auto', padding: '8px' }}>
    <Form form={form} layout="vertical">
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="firstName"
            label="First Name"
            rules={[{ required: true, message: 'Required' }]}
          >
            <Input prefix={<UserOutlined />} placeholder="First name" style={{ height: '48px' }} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="lastName"
            label="Last Name"
            rules={[{ required: true, message: 'Required' }]}
          >
            <Input prefix={<UserOutlined />} placeholder="Last name" style={{ height: '48px' }} />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item
        name="email"
        label="Email"
        rules={[
          { required: true, message: 'Required' },
          { type: 'email', message: 'Invalid email' }
        ]}
      >
        <Input prefix={<MailOutlined />} placeholder="email@example.com" style={{ height: '48px' }} />
      </Form.Item>

      <Form.Item
        name="phone"
        label="Phone Number"
        rules={[{ required: true, message: 'Required' }]}
      >
        <Input prefix={<PhoneOutlined />} placeholder="(555) 123-4567" style={{ height: '48px' }} />
      </Form.Item>

      <Divider orientation="left" style={{ marginTop: '24px', marginBottom: '16px' }}>
        <Text type="secondary" style={{ fontSize: '13px' }}>Event Location</Text>
      </Divider>

      <Form.Item
        name="eventName"
        label="Event Name"
        rules={[{ required: true, message: 'Required' }]}
      >
        <Input placeholder="Event name" style={{ height: '48px' }} />
      </Form.Item>

      <Form.Item
        name="address"
        label="Street Address"
        rules={[{ required: true, message: 'Required' }]}
      >
        <Input placeholder="Street address" style={{ height: '48px' }} />
      </Form.Item>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="city"
            label="City"
            rules={[{ required: true, message: 'Required' }]}
          >
            <Input placeholder="City" style={{ height: '48px' }} />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item
            name="state"
            label="State"
            rules={[{ required: true, message: 'Required' }]}
          >
            <Select placeholder="State" style={{ height: '48px' }}>
              {US_STATES.map(st => (
                <Option key={st} value={st}>{st}</Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item
            name="zipCode"
            label="Zip Code"
            rules={[{ required: true, message: 'Required' }]}
          >
            <Input placeholder="Zip" style={{ height: '48px' }} />
          </Form.Item>
        </Col>
      </Row>

      <Divider orientation="left" style={{ marginTop: '24px', marginBottom: '16px' }}>
        <Text type="secondary" style={{ fontSize: '13px' }}>Deployed Hearts Mobile Event</Text>
      </Divider>

      {forceMobileEvent ? (
        <Form.Item name="mobileEvent" initialValue hidden>
          <Switch />
        </Form.Item>
      ) : (
        <Form.Item
          name="mobileEvent"
          valuePropName="checked"
          initialValue={false}
          extra={
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Publish this booking to the Deployed Hearts mobile app for HCWs.
            </Text>
          }
        >
          <Switch checkedChildren="Mobile event" unCheckedChildren="Office only" />
        </Form.Item>
      )}

      <Form.Item shouldUpdate={(prev, cur) => prev.mobileEvent !== cur.mobileEvent} noStyle>
        {({ getFieldValue }) => (getFieldValue('mobileEvent') ? (
          <div>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="payRate" label="Pay Rate ($)" initialValue={0}>
                  <InputNumber min={0} style={{ width: '100%', height: '40px' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="payType" label="Pay Type" initialValue="hourly">
                  <Select>
                    <Option value="hourly">Hourly</Option>
                    <Option value="flat">Flat</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="estimatedHours" label="Est. Hours" initialValue={0}>
                  <InputNumber min={0} style={{ width: '100%', height: '40px' }} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="requiredStaff" label="Required Staff" initialValue={1}>
                  <InputNumber min={1} style={{ width: '100%', height: '40px' }} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="estimatedAttendees" label="Est. Attendees" initialValue={0}>
                  <InputNumber min={0} style={{ width: '100%', height: '40px' }} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="primaryStorageUnit" label="Primary Storage Unit">
                  <Select placeholder="Select storage unit" allowClear showSearch optionFilterProp="children">
                    {storageUnits.map(su => (
                      <Option key={su._id} value={su._id}>{su.name} ({su.code})</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="backupStorageUnit" label="Backup Storage Unit">
                  <Select placeholder="Select backup" allowClear showSearch optionFilterProp="children">
                    {storageUnits.map(su => (
                      <Option key={su._id} value={su._id}>{su.name} ({su.code})</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="geofenceRadius" label="Geofence Radius (meters)" initialValue={200}>
              <InputNumber min={0} style={{ width: '100%', height: '40px' }} />
            </Form.Item>
          </div>
        ) : null)}
      </Form.Item>

      <Divider orientation="left" style={{ marginTop: '24px', marginBottom: '16px' }}>
        <Text type="secondary" style={{ fontSize: '13px' }}>Admin Options</Text>
      </Divider>

      <Form.Item
        name="priority"
        label={
          <span>
            <FlagOutlined style={{ marginRight: '4px' }} />
            Priority
          </span>
        }
        initialValue="normal"
      >
        <Select>
          <Option value="normal">
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ background: '#d9d9d9', borderRadius: '2px', padding: '0 7px', fontSize: '12px' }}>Normal</span>
            </span>
          </Option>
          <Option value="high">
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ background: '#fff7e6', color: '#d46b08', borderRadius: '2px', padding: '0 7px', fontSize: '12px' }}>High</span>
            </span>
          </Option>
          <Option value="urgent">
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ background: '#fff1f0', color: '#cf1322', borderRadius: '2px', padding: '0 7px', fontSize: '12px' }}>Urgent</span>
            </span>
          </Option>
        </Select>
      </Form.Item>

      <Form.Item
        name="adminNotes"
        label="Admin Notes (Optional)"
        extra={
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {form.getFieldValue('adminNotes')?.length || 0} / 500 characters
          </Text>
        }
      >
        <TextArea
          rows={3}
          maxLength={500}
          placeholder="Internal notes about this booking (visible to admin users only)..."
          showCount={false}
        />
      </Form.Item>

      <Form.Item name="skipConfirmationEmail" valuePropName="checked">
        <Checkbox>
          Skip sending confirmation email to client
        </Checkbox>
      </Form.Item>

      {/* Booking Summary */}
      <Card size="small" style={{ background: '#f5f5f5', marginTop: '16px' }}>
        <Text strong>Booking Summary</Text>
        <div style={{ marginTop: '8px' }}>
          <div><Text type="secondary">Event:</Text> {formValues?.eventName || 'Not specified'}</div>
          <div>
            <Text type="secondary">Date:</Text> {selectedDate?.format('MMMM D, YYYY')}
            {selectedEndDate && (
              <span> – {selectedEndDate.format('MMMM D, YYYY')}
                <span style={{ 
                  marginLeft: '8px', 
                  color: '#1890ff', 
                  fontSize: '12px',
                  background: '#e6f7ff',
                  padding: '1px 8px',
                  borderRadius: '4px'
                }}>
                  {selectedEndDate.diff(selectedDate, 'days') + 1} days
                </span>
              </span>
            )}
          </div>
          <div><Text type="secondary">Time:</Text> {moment(selectedTimeSlot?.startTime).format('h:mm A')}</div>
          <div><Text type="secondary">Duration:</Text> {formatDuration(selectedEventType?.duration)}</div>
        </div>
      </Card>
    </Form>
  </div>
);

export default Step2UserInfo;
